import { Box, Stack, Typography, useTheme } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsSolidGauge from 'highcharts/modules/solid-gauge';

import { RADIAL_SIZE_CONFIG, type EmmRadialSize } from '../../theme/emm/sizes';
import { emmTint } from '../../theme/emm/tint';

import type { SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';

// SolidGauge depends on highcharts-more. Initialize both once at import time.
HighchartsMore(Highcharts);
HighchartsSolidGauge(Highcharts);

export interface EmmGaugeBand {
  /** Range start (inclusive). */
  from: number;
  /** Range end (inclusive). */
  to: number;
  /** Resolved color for this band. */
  color: string;
  /** Optional label rendered in the legend below the gauge. */
  label?: string;
}

export type EmmGaugeSize = EmmRadialSize;

export interface EmmGaugeProps {
  value: number;
  /** Scale minimum. Default `0`. */
  min?: number;
  /** Scale maximum. Default `100`. */
  max?: number;
  /**
   * Color bands. Drive (a) the arc color — it matches whichever band the value
   * sits in — and (b) the legend rendered below the gauge. The bands are
   * deliberately not painted onto the gauge backdrop: Highcharts `plotBands`
   * have squared-off ends and can't be rounded, which would break the rounded
   * end caps that the rest of the gauge has.
   */
  bands?: EmmGaugeBand[];
  /** Color for the value arc. Default `primary.main`. Ignored when `bands` is set — the arc color matches the band the value sits in. */
  color?: string;
  /** Big number shown in the gauge center (e.g. `"72%"`). Omit to render no center text. */
  centerValue?: string;
  /** Small caption below the center value. */
  centerLabel?: string;
  size?: EmmGaugeSize;
  sx?: SxProps<Theme>;
}

const resolveBandColor = (value: number, bands: EmmGaugeBand[] | undefined, fallback: string): string => {
  if (!bands || bands.length === 0) return fallback;
  const match = bands.find(b => value >= b.from && value <= b.to);
  return match?.color ?? fallback;
};

export const EmmGauge = ({
  value,
  min = 0,
  max = 100,
  bands,
  color,
  centerValue,
  centerLabel,
  size = 'lg',
  sx
}: EmmGaugeProps) => {
  const theme = useTheme();
  const cfg = RADIAL_SIZE_CONFIG[size];

  const resolvedArcColor = color ?? resolveBandColor(value, bands, theme.palette.primary.main);

  // Mode-aware neutral track — `medium` (0.15) on the foreground, same as the ring/donut tracks.
  const trackColor = emmTint(theme, 'medium');

  // Ring thickness expressed as inner-radius percentage so it matches EmmCircularProgress's stroke (12px on a 240-radius circle = ~10% of radius).
  const innerRadiusPct = `${Math.round(((cfg.diameter / 2 - cfg.thickness) / (cfg.diameter / 2)) * 100)}%`;

  // Two series so both the unfilled track AND the value arc get rounded ends.
  // (Pane `background` and yAxis `plotBands` have no rounded variant.)
  const trackSeries: Highcharts.SeriesSolidgaugeOptions = {
    type: 'solidgauge',
    data: [{ y: max, color: trackColor }],
    innerRadius: innerRadiusPct,
    radius: '100%',
    enableMouseTracking: false,
    showInLegend: false
  };
  const valueSeries: Highcharts.SeriesSolidgaugeOptions = {
    type: 'solidgauge',
    data: [{ y: value, color: resolvedArcColor }],
    innerRadius: innerRadiusPct,
    radius: '100%'
  };

  const options: Highcharts.Options = {
    chart: {
      type: 'solidgauge',
      backgroundColor: 'transparent',
      height: cfg.diameter,
      width: cfg.diameter,
      spacing: [0, 0, 0, 0],
      margin: [0, 0, 0, 0],
      style: { fontFamily: theme.typography.fontFamily }
    },
    title: { text: undefined },
    credits: { enabled: false },
    accessibility: { enabled: false },
    tooltip: { enabled: false },
    pane: {
      // Clean half-donut: open at the bottom. No pane background — the track
      // series gives us a rounded unfilled portion.
      startAngle: -90,
      endAngle: 90,
      background: []
    },
    yAxis: {
      min,
      max,
      lineWidth: 0,
      tickPositions: [],
      labels: { enabled: false }
    },
    plotOptions: {
      solidgauge: {
        dataLabels: { enabled: false },
        linecap: 'round',
        stickyTracking: false,
        rounded: true
      }
    },
    series: [trackSeries, valueSeries]
  };

  // Visible band = top half of the chart + half the ring thickness so the
  // rounded endpoint caps are fully visible and the text below aligns with the
  // bottom of those caps (not the bottom of the chart's plotting area).
  const visibleHeight = Math.ceil(cfg.diameter / 2 + cfg.thickness / 2);
  const hasCenter = centerValue != null || centerLabel != null;

  return (
    <Stack alignItems="center" spacing={1.5} sx={{ display: 'inline-flex', ...sx }}>
      <Box sx={{ position: 'relative', width: cfg.diameter, height: visibleHeight, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
          <HighchartsReact highcharts={Highcharts} options={options} immutable />
        </Box>
        {hasCenter && (
          <Stack
            alignItems="center"
            spacing={0.25}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              // Anchor to the bottom of the visible band so the text sits in
              // the gauge's opening, not inside the arc.
              bottom: 0,
              pointerEvents: 'none'
            }}
          >
            {centerValue && (
              <Typography variant={cfg.centerValueVariant} sx={{ lineHeight: 1 }}>
                {centerValue}
              </Typography>
            )}
            {centerLabel && (
              <Typography variant={cfg.centerLabelVariant} color="text.secondary">
                {centerLabel}
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      {bands && bands.some(b => b.label) && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" useFlexGap>
          {bands.reduce<ReactNode[]>((acc, b) => {
            if (b.label) {
              acc.push(
                <Stack key={`${b.from}-${b.to}`} direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">
                    {b.label}
                  </Typography>
                </Stack>
              );
            }
            return acc;
          }, [])}
        </Stack>
      )}
    </Stack>
  );
};
